import React, { memo, useState } from 'react';
import {
  Button,
  DialogContentText,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';
import { ReactMultiEmail } from 'react-multi-email';
import 'react-multi-email/dist/style.css';
import BaseModal from './common/BaseModal';
import { getSentence } from './translations';
import { shareMeeting } from './Network';

const ShareDialog = memo(({
  open,
  meetingId,
  onClose
}) => {
  const [emails, setEmails] = useState([]);
  const [shareOptions, setShareOptions] = useState({
    summary: false,
    keypoints: false,
    transcription: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckboxChange = (option) => (event) => {
    setShareOptions(prev => ({
      ...prev,
      [option]: event.target.checked
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    const formData = new FormData();
    formData.append("email", emails.join(','));
    
    // Add selected options to form data
    Object.entries(shareOptions).forEach(([key, value]) => {
      if (value) {
        formData.append(key, 'on');
      }
    });

    try {
      await shareMeeting(meetingId, formData);
      handleClose();
    } catch (error) {
      console.error('Error sharing meeting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form
    setEmails([]);
    setShareOptions({
      summary: false,
      keypoints: false,
      transcription: false
    });
    setIsLoading(false);
  };

  const actions = (
    <>
      <Button 
        onClick={handleClose}
        variant="outlined"
        disabled={isLoading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {getSentence(40) || 'Cancel'}
      </Button>
      <Button 
        onClick={handleSubmit}
        variant="contained"
        disabled={emails.length === 0 || !Object.values(shareOptions).some(Boolean) || isLoading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {isLoading ? 'Sharing...' : (getSentence(39) || 'Share')}
      </Button>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={getSentence(37) || 'Share Meeting'}
      size="medium"
      maxWidth="sm"
      actions={actions}
    >
      <DialogContentText sx={{ mb: 2 }}>
        {getSentence(37) || 'Select what to share and enter email addresses:'}
      </DialogContentText>
      
      <FormGroup sx={{ mb: 3 }}>
        <FormControlLabel 
          control={
            <Checkbox 
              checked={shareOptions.summary}
              onChange={handleCheckboxChange('summary')}
              disabled={isLoading}
            />
          } 
          label={getSentence(3) || 'Summary'} 
        />
        <FormControlLabel 
          control={
            <Checkbox 
              checked={shareOptions.keypoints}
              onChange={handleCheckboxChange('keypoints')}
              disabled={isLoading}
            />
          } 
          label={getSentence(4) || 'Key Points'} 
        />
        <FormControlLabel 
          control={
            <Checkbox 
              checked={shareOptions.transcription}
              onChange={handleCheckboxChange('transcription')}
              disabled={isLoading}
            />
          } 
          label={getSentence(5) || 'Transcription'} 
        />
      </FormGroup>
      
      <Box sx={{ mt: 2 }}>
        <ReactMultiEmail
          placeholder={getSentence(38) || 'Enter email addresses...'}
          emails={emails}
          onChange={setEmails}
          delimiter={['/', ',', ';', ' ']}
          autoFocus
          disabled={isLoading}
          style={{
            minHeight: '60px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px',
            opacity: isLoading ? 0.6 : 1
          }}
          onKeyDown={(e) => {
            // Custom handler: validate input when pressing space or enter
            if (e.key === ' ' || e.key === ',' || e.key === ';') {
              e.preventDefault(); // prevent just adding a space character
              const input = e.target.value.trim();
              if (input && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) {
                setEmails((prev) => [...prev, input]);
                e.target.value = '';
                console.log(e.target)
              }
            }
          }}
          getLabel={(email, index, removeEmail) => (
            <div data-tag key={index}>
              <div data-tag-item>{email}</div>
              <span 
                data-tag-handle 
                onClick={() => !isLoading && removeEmail(index)}
                style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                ×
              </span>
            </div>
          )}
        />
      </Box>
    </BaseModal>
  );
});

ShareDialog.displayName = 'ShareDialog';

export default ShareDialog; 